// Identity types

/**
 * Tenant role enum matching the API TenantRole enum
 */
export enum TenantRole {
  Owner = 'Owner',
  Admin = 'Admin',
  SecurityEngineer = 'SecurityEngineer',
  Developer = 'Developer',
  Auditor = 'Auditor',
  Viewer = 'Viewer',
}

/**
 * User status enum
 */
export enum UserStatus {
  Active = 'Active',
  Inactive = 'Inactive',
  Suspended = 'Suspended',
  PendingVerification = 'PendingVerification',
}

/**
 * Identity provider enum
 */
export enum IdentityProvider {
  Local = 'Local',
  AzureAD = 'AzureAD',
  Google = 'Google',
  GitHub = 'GitHub',
  Okta = 'Okta',
  SAML = 'SAML',
}

/**
 * User DTO matching API UserDto
 */
export interface User {
  id: string;
  externalId: string;
  email: string;
  displayName: string;
  firstName?: string;
  lastName?: string;
  identityProvider: IdentityProvider;
  status: UserStatus;
  createdAt: string;
  lastLoginAt?: string;
}

/**
 * Tenant membership DTO
 */
export interface TenantMembership {
  tenantId: string;
  tenantName: string;
  role: TenantRole;
  joinedAt: string;
}

/**
 * User with tenant memberships
 */
export interface UserWithMemberships extends User {
  memberships: TenantMembership[];
}

/**
 * Invitation status enum
 */
export enum InvitationStatus {
  Pending = 'Pending',
  Accepted = 'Accepted',
  Expired = 'Expired',
  Revoked = 'Revoked',
}

/**
 * Invitation DTO matching API InvitationDto
 */
export interface Invitation {
  id: string;
  tenantId: string;
  tenantName: string;
  email: string;
  role: TenantRole;
  status: InvitationStatus;
  invitedById: string;
  invitedByName?: string;
  createdAt: string;
  expiresAt: string;
  acceptedAt?: string;
}

/**
 * Create invitation request
 */
export interface CreateInvitationRequest {
  tenantId: string;
  email: string;
  role: TenantRole;
}

/**
 * Accept invitation request
 */
export interface AcceptInvitationRequest {
  token: string;
}

/**
 * Accept invitation result
 */
export interface AcceptInvitationResult {
  tenantId: string;
  tenantName: string;
  role: string;
}

/**
 * Update user role request
 */
export interface UpdateUserRoleRequest {
  tenantId: string;
  userId: string;
  newRole: TenantRole;
}

/**
 * Update user role result
 */
export interface UpdateUserRoleResult {
  userId: string;
  tenantId: string;
  oldRole: TenantRole;
  newRole: TenantRole;
}

/**
 * Permission definition
 */
export interface Permission {
  resource: string;
  action: string;
  fullName: string;
}

/**
 * Standard permissions available in the system
 */
export const Permissions = {
  // Users
  UsersRead: { resource: 'users', action: 'read', fullName: 'users:read' },
  UsersWrite: { resource: 'users', action: 'write', fullName: 'users:write' },
  UsersDelete: { resource: 'users', action: 'delete', fullName: 'users:delete' },

  // Compliance
  ComplianceRead: { resource: 'compliance', action: 'read', fullName: 'compliance:read' },
  ComplianceWrite: { resource: 'compliance', action: 'write', fullName: 'compliance:write' },

  // Security
  SecurityRead: { resource: 'security', action: 'read', fullName: 'security:read' },
  SecurityWrite: { resource: 'security', action: 'write', fullName: 'security:write' },

  // Tenants
  TenantsRead: { resource: 'tenants', action: 'read', fullName: 'tenants:read' },
  TenantsWrite: { resource: 'tenants', action: 'write', fullName: 'tenants:write' },
  TenantsDelete: { resource: 'tenants', action: 'delete', fullName: 'tenants:delete' },

  // Settings
  SettingsRead: { resource: 'settings', action: 'read', fullName: 'settings:read' },
  SettingsWrite: { resource: 'settings', action: 'write', fullName: 'settings:write' },
} as const;

/**
 * API result wrapper
 */
export interface ApiResult<T> {
  isSuccess: boolean;
  value?: T;
  message?: string;
  statusCode: number;
}

/**
 * Tenant member - a user who is a member of a tenant
 */
export interface TenantMember {
  userId: string;
  tenantId: string;
  email: string;
  displayName: string;
  role: TenantRole;
  status: UserStatus;
  joinedAt: string;
  lastActiveAt?: string;
}

/**
 * Remove member request
 */
export interface RemoveMemberRequest {
  tenantId: string;
  userId: string;
}
